import { z } from "zod";

import {
  responseErrorSchema,
  visitOccurrenceSchema,
  VisitOccurrenceStatus,
  VisitType,
} from "../Cases/types";

export const visitOccurrencesFiltersSchema = z.object({
  displayId: z.string().optional(),
  workerId: z.string().optional(),
  status: z.nativeEnum(VisitOccurrenceStatus).optional(),
  visitId: z.string().optional(),
  completedAt: z
    .object({
      gte: z.string().optional(),
      lte: z.string().optional(),
    })
    .optional(),
  externalPatientIdentifier: z
    .object({
      eq: z.string().optional(),
      contains: z.string().optional(),
      startsWith: z.string().optional(),
      endsWith: z.string().optional(),
      in: z.array(z.string()).optional(),
    })
    .optional(),
  rejectionReason: z
    .object({
      eq: z.string().optional(),
      contains: z.string().optional(),
      startsWith: z.string().optional(),
      endsWith: z.string().optional(),
      in: z.array(z.string()).optional(),
    })
    .optional(),
  actionedAt: z
    .object({
      gte: z.string().optional(),
      lte: z.string().optional(),
    })
    .optional(),
  actionedBy: z
    .object({
      eq: z.string().optional(),
      contains: z.string().optional(),
      startsWith: z.string().optional(),
      endsWith: z.string().optional(),
      in: z.array(z.string()).optional(),
    })
    .optional(),
  chargeRate: z
    .object({
      eq: z.number().optional(),
      gte: z.number().optional(),
      lte: z.number().optional(),
    })
    .optional(),
  payRate: z
    .object({
      eq: z.number().optional(),
      gte: z.number().optional(),
      lte: z.number().optional(),
    })
    .optional(),
  visitType: z
    .object({
      eq: z.nativeEnum(VisitType).optional(),
      in: z.array(z.nativeEnum(VisitType)).optional(),
    })
    .optional(),
});
export type VisitOccurrencesFilters = z.infer<typeof visitOccurrencesFiltersSchema>;

// eslint-disable-next-line no-restricted-syntax
export enum VisitOccurrencesSort {
  CREATED_AT = "createdAt",
  COMPLETED_AT = "completedAt",
  ACTIONED_AT = "actionedAt",
  REJECTION_REASON = "rejectionReason",
  EXTERNAL_PATIENT_IDENTIFIER = "externalPatientIdentifier",
  VISIT_TYPE = "visitType",
}

export const visitOccurrencesSortSchema = z.enum(
  Object.values(VisitOccurrencesSort).flatMap((value) => [value, `-${value}`]) as [
    string,
    ...string[],
  ]
);

export const visitOccurrencesPageSchema = z.object({
  number: z.number().optional(),
  size: z.number().optional(),
});

export const visitOccurrencesParamsSchema = z.object({
  filter: visitOccurrencesFiltersSchema.optional(),
  sort: visitOccurrencesSortSchema.optional(),
  page: visitOccurrencesPageSchema.optional(),
});
export type VisitOccurrencesParams = z.infer<typeof visitOccurrencesParamsSchema>;

export const visitOccurrencesResponseSchema = z.object({
  data: z.array(visitOccurrenceSchema),
  links: z
    .object({
      next: z.string().optional(),
      prev: z.string().optional(),
    })
    .optional(),
  errors: z.array(responseErrorSchema).optional(),
});
export type VisitOccurrencesResponse = z.infer<typeof visitOccurrencesResponseSchema>;

export const userIdSchema = z.object({
  type: z.union([z.literal("workerId"), z.literal("userId")]),
  id: z.string(),
});

export const visitOccurrencesUsersResponseSchema = z.object({
  data: z.array(userIdSchema),
  errors: z.array(responseErrorSchema).optional(),
});

export const updateVisitOccurrenceRequestSchema = z.object({
  data: visitOccurrenceSchema.omit({ id: true }).extend({
    attributes: z.object({
      status: z.nativeEnum(VisitOccurrenceStatus).optional(),
      rejectionReason: z.string().optional(),
      paid: z.boolean().optional(),
      payRate: z
        .object({
          amountInMinorUnits: z.number(),
          currencyCode: z.literal("USD"),
        })
        .optional(),
      chargeRate: z
        .object({
          amountInMinorUnits: z.number(),
          currencyCode: z.literal("USD"),
        })
        .optional(),
    }),
  }),
});
export type UpdateVisitOccurrenceRequest = z.infer<typeof updateVisitOccurrenceRequestSchema>;

export const updateVisitOccurrenceResponseSchema = z.object({
  data: visitOccurrenceSchema,
  errors: z.array(responseErrorSchema).optional(),
});
export type UpdateVisitOccurrenceResponse = z.infer<typeof updateVisitOccurrenceResponseSchema>;
